import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Calendar, MapPin, Users, Euro, FileText, Upload, Check, Download, Share2, Mail, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import DynamicTileLayer from '@/components/DynamicTileLayer';
import MapSelector from '@/components/MapSelector';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Helper function to get correct URL (handles both Cloudinary and local URLs)
const getFileUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url; // Cloudinary URL
  return `${process.env.REACT_APP_BACKEND_URL}/api${url}`; // Local URL
};

// Component to add fullscreen control
function FullscreenControl() {
  const map = useMap();

  useEffect(() => {
    // Create custom fullscreen control
    const FullscreenControl = L.Control.extend({
      onAdd: function() {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-custom');
        container.style.backgroundColor = 'white';
        container.style.width = '34px';
        container.style.height = '34px';
        container.style.cursor = 'pointer';
        container.style.display = 'flex';
        container.style.alignItems = 'center';
        container.style.justifyContent = 'center';
        container.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path></svg>';
        container.title = 'Ver en pantalla completa';
        
        container.onclick = function() {
          const mapContainer = map.getContainer();
          if (!document.fullscreenElement) {
            mapContainer.requestFullscreen().then(() => {
              container.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path></svg>';
              container.title = 'Salir de pantalla completa';
              setTimeout(() => map.invalidateSize(), 100);
            });
          } else {
            document.exitFullscreen().then(() => {
              container.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path></svg>';
              container.title = 'Ver en pantalla completa';
              setTimeout(() => map.invalidateSize(), 100);
            });
          }
        };
        
        return container;
      }
    });

    const fullscreenControl = new FullscreenControl({ position: 'topleft' });
    map.addControl(fullscreenControl);

    return () => {
      map.removeControl(fullscreenControl);
    };
  }, [map]);

  return null;
}

// Component to load and display multiple GPX tracks
function MultiGPXLayer({ tracks, visibleTracks }) {
  const map = useMap();
  const polylinesRef = useRef([]);
  const markersRef = useRef([]);
  
  // Track colors
  const colors = ['#f97316', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6', '#f59e0b', '#ec4899'];

  useEffect(() => {
    // Clear previous polylines and markers
    polylinesRef.current.forEach(polyline => map.removeLayer(polyline));
    markersRef.current.forEach(marker => map.removeLayer(marker));
    polylinesRef.current = [];
    markersRef.current = [];

    const loadAllTracks = async () => {
      const allBounds = [];

      for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i];
        if (!track || !track.file || !visibleTracks[track.number - 1]) continue;

        try {
          const gpxUrl = getFileUrl(track.file);
          console.log(`Loading GPX from: ${gpxUrl}`);
          const response = await fetch(gpxUrl);
          
          if (!response.ok) {
            console.error(`Failed to load track ${track.number}: ${response.status} ${response.statusText}`);
            continue;
          }
          
          const gpxText = await response.text();
          
          // Parse GPX
          const parser = new DOMParser();
          const gpx = parser.parseFromString(gpxText, 'text/xml');
          
          // Extract track points
          let trkpts = gpx.getElementsByTagName('trkpt');
          if (trkpts.length === 0) trkpts = gpx.getElementsByTagName('rtept');
          if (trkpts.length === 0) trkpts = gpx.getElementsByTagName('wpt');
          
          const coordinates = [];
          for (let j = 0; j < trkpts.length; j++) {
            const lat = parseFloat(trkpts[j].getAttribute('lat'));
            const lon = parseFloat(trkpts[j].getAttribute('lon'));
            if (!isNaN(lat) && !isNaN(lon)) {
              coordinates.push([lat, lon]);
            }
          }
          
          console.log(`Track ${track.number} loaded: ${coordinates.length} points`);
          
          if (coordinates.length > 0) {
            // Create polyline with unique color
            const color = colors[i % colors.length];
            const polyline = L.polyline(coordinates, {
              color: color,
              weight: 4,
              opacity: 0.8,
              smoothFactor: 1
            }).addTo(map);
            
            polylinesRef.current.push(polyline);
            allBounds.push(polyline.getBounds());
            
            // Add start marker
            const startMarker = L.marker(coordinates[0], {
              icon: L.divIcon({
                className: 'custom-marker',
                html: `<div style="background-color: ${color}; width: 20px; height: 20px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
                iconSize: [20, 20]
              })
            }).addTo(map).bindPopup(`${track.name} - Inicio`);
            
            markersRef.current.push(startMarker);
          }
        } catch (error) {
          console.error(`Error loading track ${track.number}:`, error);
        }
      }

      // Fit map to all visible tracks
      if (allBounds.length > 0) {
        const combinedBounds = allBounds.reduce((acc, bounds) => acc.extend(bounds), L.latLngBounds());
        map.fitBounds(combinedBounds, { padding: [50, 50] });
      }
    };

    loadAllTracks();

    // Cleanup on unmount
    return () => {
      polylinesRef.current.forEach(polyline => map.removeLayer(polyline));
      markersRef.current.forEach(marker => map.removeLayer(marker));
    };
  }, [tracks, visibleTracks, map]);

  return null;
}

export default function EventDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [isAlreadyRegistered, setIsAlreadyRegistered] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [acceptedRules, setAcceptedRules] = useState(false);
  const [motorcycles, setMotorcycles] = useState([]);
  const [motoSelection, setMotoSelection] = useState('');
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    full_name: user.full_name || '',
    dni: '',
    phone: user.phone || '',
    physical_address: '',
    email: user.email || '',
    moto_brand: '',
    moto_model: '',
    moto_plate: '',
    moto_years: '',
    moto_kilometers: '',
    discount_code: '',
    iba_number: '',
    arem_number: '',
    aemotur_number: '',
    tshirt_size: ''
  });

  // Odometer motorcycles state (up to 5)
  const [odometerMotos, setOdometerMotos] = useState([
    { brand: '', model: '', kilometers: '', photo: null }
  ]);

  useEffect(() => {
    fetchMotorcycles();
  }, []);

  // Mostrar formulario solo cuando se navega a #registro
  useEffect(() => {
    if (hash === '#registro') {
      setShowRegistrationForm(true);
    }
  }, [hash]);

  const [files, setFiles] = useState({
    circulation_permit: null,
    technical_sheet: null,
    insurance: null
  });
  
  // Tracks state
  const [availableTracks, setAvailableTracks] = useState([]);
  const [visibleTracks, setVisibleTracks] = useState([]);
  const [showAllTracks, setShowAllTracks] = useState(false);
  
  // Gallery states
  const [showGallery, setShowGallery] = useState(false);
  const [galleryPhotos, setGalleryPhotos] = useState([]);

  useEffect(() => {
    fetchEvent();
    fetchMotorcycles();
  }, [id]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/events/${id}`);
      setEvent(response.data);
      
      // Extract available tracks from event
      const tracks = [];
      for (let i = 1; i <= 7; i++) {
        const trackName = response.data[`track${i}_name`];
        const trackFile = response.data[`track${i}_file`];
        if (trackFile) {
          tracks.push({
            number: i,
            name: trackName || `Track ${i}`,
            file: trackFile
          });
        }
      }
      setAvailableTracks(tracks);
      // Show all tracks by default
      setVisibleTracks(new Array(7).fill(true));
      setShowAllTracks(true);
      
      // Check if user is already registered for this event
      if (user) {
        try {
          const registrationsResponse = await axios.get(`${API}/registrations/my`);
          const isRegistered = registrationsResponse.data.some(reg => reg.event_id === id);
          setIsAlreadyRegistered(isRegistered);
        } catch (error) {
          console.error('Error checking registration status:', error);
        }
      }
    } catch (error) {
      toast.error('Error al cargar el evento');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchMotorcycles = async () => {
    try {
      const response = await axios.get(`${API}/motorcycles`);
      setMotorcycles(response.data);
    } catch (error) {
      console.error('Error loading motorcycles:', error);
    }
  };

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/events/${id}/gallery`);
      setGalleryPhotos(response.data.photos || []);
      setShowGallery(true);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Solo los usuarios registrados pueden ver la galería');
      } else {
        toast.error('Error al cargar la galería');
      }
    }
  };

  const handleFileChange = (field, file) => {
    setFiles({ ...files, [field]: file });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!acceptedTerms) {
      toast.error('Debes aceptar las Recomendaciones y Normas');
      return;
    }

    if (!acceptedPrivacy) {
      toast.error('Debes aceptar la política de privacidad');
      return;
    }

    if (!acceptedRules) {
      toast.error('Debes aceptar las Bases de Participación');
      return;
    }

    // Only validate documents if event requires them AND user has a motorcycle
    // Skip document validation if user selected "Sin Moto"
    if (event.requires_documents !== false && motoSelection !== 'none') {
      if (!files.circulation_permit || !files.technical_sheet || !files.insurance) {
        toast.error('Debes subir todos los documentos requeridos');
        return;
      }
    }

    setRegistering(true);

    try {
      const registrationData = {
        ...formData,
        event_id: id,
        moto_selection: motoSelection
      };

      // Add motorcycle_id if selecting from saved motorcycles
      if (motoSelection !== 'rental' && motoSelection !== 'other' && motoSelection !== 'none') {
        registrationData.motorcycle_id = motoSelection;
      }

      // Add odometer motorcycles if event requires them
      if (event.requires_odometer_photo) {
        registrationData.odometer_motorcycles = odometerMotos.filter(m => m.brand && m.model);
      }

      const formDataToSend = new FormData();
      formDataToSend.append('registration_data', JSON.stringify(registrationData));
      
      // Only append files if they exist (could be File objects or URLs from saved moto)
      // If they're URLs (strings), they'll be handled by backend via motorcycle_id
      if (files.circulation_permit && typeof files.circulation_permit !== 'string') {
        formDataToSend.append('circulation_permit', files.circulation_permit);
      }
      if (files.technical_sheet && typeof files.technical_sheet !== 'string') {
        formDataToSend.append('technical_sheet', files.technical_sheet);
      }
      if (files.insurance && typeof files.insurance !== 'string') {
        formDataToSend.append('insurance', files.insurance);
      }

      // Append odometer photos
      if (event.requires_odometer_photo) {
        odometerMotos.forEach((moto, index) => {
          if (moto.photo) {
            formDataToSend.append(`odometer_photo_${index + 1}`, moto.photo);
          }
        });
      }

      const response = await axios.post(`${API}/registrations`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Show success message first
      toast.success('¡Inscripción realizada con éxito!', {
        duration: 6000
      });
      
      // If payment required, show info message after a delay
      if (response.data.requires_payment) {
        setTimeout(() => {
          toast.info('Recibirás un email con el importe de la inscripción y las instrucciones para realizar el pago. Recuerda que sólo tendrás confirmada tu asistencia y guardada tu plaza una vez hayamos verificado tu pago.', {
            duration: 8000
          });
        }, 500);
      }
      
      // Navigate after messages have been shown
      const navigationDelay = response.data.requires_payment ? 3000 : 2000;
      setTimeout(() => {
        navigate('/my-registrations');
      }, navigationDelay);
    } catch (error) {
      // Clear previous errors
      setErrors({});
      
      // Handle validation errors from backend
      if (error.response?.status === 422 && error.response?.data?.detail) {
        const validationErrors = {};
        const details = error.response.data.detail;
        
        // Parse Pydantic validation errors
        if (Array.isArray(details)) {
          details.forEach(err => {
            const field = err.loc?.[1] || err.loc?.[0]; // Get field name
            validationErrors[field] = err.msg;
          });
          setErrors(validationErrors);
          toast.error('Por favor, corrige los campos marcados en rojo');
        } else {
          toast.error(details);
        }
      } else {
        toast.error(error.response?.data?.detail || 'Error en la inscripción. Por favor, revisa todos los campos.');
      }
      
      // Scroll to first error
      setTimeout(() => {
        const firstError = document.querySelector('.border-red-500');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Cargando...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-gray-100 py-12 px-4">
      <div className="max-w-5xl mx-auto space-y-8">
        {/* Event Info */}
        <Card className="border-0 shadow-xl overflow-hidden" data-testid="event-detail-card">
          {event.poster_image && (
            <div className="h-64 overflow-hidden bg-gradient-to-br from-orange-400 to-gray-600">
              <img 
                src={getFileUrl(event.poster_image)}
                alt={event.title} 
                className="w-full h-full object-cover"
              />
            </div>
          )}
          
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <CardTitle className="text-3xl mb-2" data-testid="event-detail-title">{event.title}</CardTitle>
                <CardDescription className="text-base" data-testid="event-detail-description">{event.description}</CardDescription>
                
                {/* Share Buttons */}
                <div className="flex flex-wrap items-center gap-2 mt-4">
                  <span className="text-sm text-gray-600 font-semibold flex items-center gap-1">
                    <Share2 className="w-4 h-4" />
                    Compartir:
                  </span>
                  
                  {/* WhatsApp - Using anchor tag for better mobile compatibility */}
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(`¡Mira este evento de mototurismo! ${event.title} ${window.location.href}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors"
                    title="Compartir por WhatsApp"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp
                  </a>

                  {/* Telegram - Using anchor tag for better compatibility */}
                  <a
                    href={`https://t.me/share/url?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(`¡Mira este evento de mototurismo! ${event.title}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors"
                    title="Compartir por Telegram"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                    </svg>
                    Telegram
                  </a>

                  {/* Email */}
                  <a
                    href={`mailto:?subject=${encodeURIComponent(`Te invito a este evento: ${event.title}`)}&body=${encodeURIComponent(`¡Hola!\n\nTe invito a participar en este evento de mototurismo:\n\n${event.title}\n${event.description?.substring(0, 200) || ''}...\n\nFecha: ${event.start_date} al ${event.end_date}\n\nMás información:\n${window.location.href}`)}`}
                    className="flex items-center gap-1 px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors"
                    title="Compartir por Email"
                  >
                    <Mail className="w-4 h-4" />
                    Email
                  </a>
                </div>
              </div>
              {event.requires_payment && (
                <Badge className="text-lg px-4 py-2 bg-orange-100 text-orange-700" data-testid="event-detail-price">
                  {event.price}€
                </Badge>
              )}
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {event.organizer && (
                <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                  <MapPin className="w-5 h-5 text-orange-500" />
                  <div>
                    <p className="text-xs text-gray-600">Organizador</p>
                    <p className="font-semibold text-sm">{event.organizer}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                <Calendar className="w-5 h-5 text-orange-500" />
                <div>
                  <p className="text-xs text-gray-600">Fecha Inicio</p>
                  <p className="font-semibold text-sm" data-testid="event-detail-date">
                    {event.start_date}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                <Calendar className="w-5 h-5 text-orange-500" />
                <div>
                  <p className="text-xs text-gray-600">Fecha Final</p>
                  <p className="font-semibold text-sm">
                    {event.end_date}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                <Users className="w-5 h-5 text-orange-500" />
                <div>
                  <p className="text-xs text-gray-600">Participantes</p>
                  <p className="font-semibold" data-testid="event-detail-participants">Máx. {event.max_participants}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="text-xs text-gray-600">Política de Cancelación</p>
                  <p className="font-semibold text-sm" data-testid="event-detail-cancellation">Hasta {event.cancellation_days || 7} días antes</p>
                </div>
              </div>
              <div className="flex flex-col gap-1 p-3 bg-orange-50 rounded-lg">
                <p className="text-xs text-gray-600">Tipo de Ruta</p>
                <Badge className="bg-orange-500 text-white w-fit text-xs">
                  {event.route_type === 'off_road' && 'Off Road'}
                  {event.route_type === 'trail' && 'Trail (Mixto)'}
                  {event.route_type === 'on_road' && 'On Road'}
                  {event.route_type === 'encuentro' && 'Encuentro'}
                </Badge>
              </div>
              <div className="flex flex-col gap-1 p-3 bg-orange-50 rounded-lg">
                <p className="text-xs text-gray-600">Categoría</p>
                <Badge className={`w-fit text-xs ${
                  event.category === 'easy' ? 'bg-green-500 text-white' :
                  event.category === 'intermediate' ? 'bg-yellow-500 text-white' :
                  event.category === 'sin_conduccion' ? 'bg-blue-500 text-white' :
                  'bg-red-500 text-white'
                }`}>
                  {event.category === 'easy' && 'Fácil'}
                  {event.category === 'intermediate' && 'Intermedio'}
                  {event.category === 'expert' && 'Experto'}
                  {event.category === 'sin_conduccion' && 'Sin Conducción'}
                </Badge>
              </div>
              <div className="flex flex-col gap-1 p-3 bg-orange-50 rounded-lg">
                <p className="text-xs text-gray-600">Estilo de Ruta</p>
                <Badge className="bg-purple-500 text-white w-fit text-xs">
                  {event.style === 'slowride' && 'Slowride / Mototurismo'}
                  {event.style === 'insaneride' && 'Insaneride / Resistencia'}
                  {event.style === 'celebracion' && 'Noride / Amistad'}
                </Badge>
              </div>
            </div>

            <div className="pt-4 space-y-3">
              <div className="flex flex-wrap gap-2">
                <Button 
                  onClick={() => setShowTerms(true)}
                  variant="outline"
                  className="flex-1 md:flex-none"
                  data-testid="view-terms-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Recomendaciones y Normas
                </Button>
                <Button 
                  onClick={() => setShowRules(true)}
                  variant="outline"
                  className="flex-1 md:flex-none"
                  data-testid="view-rules-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Bases de Participación
                </Button>
                <Button 
                  onClick={() => setShowPrivacyPolicy(true)}
                  variant="outline"
                  className="flex-1 md:flex-none"
                  data-testid="view-privacy-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Política de Privacidad y Uso de Imagen
                </Button>
                {event.info_pdf && (
                  <Button
                    onClick={() => window.open(getFileUrl(event.info_pdf), '_blank')}
                    className="flex-1 md:flex-none bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                    data-testid="download-pdf-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Descargar PDF Informativo
                  </Button>
                )}
              </div>
              
              {/* Download PDFs Section */}
              {(event.terms_pdf || event.rules_pdf || event.privacy_pdf) && (
                <div className="border-t pt-3">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Descargar documentos:</p>
                  <div className="flex flex-wrap gap-2">
                    {event.terms_pdf && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${event.terms_pdf}`}
                        download
                        className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Recomendaciones PDF
                      </a>
                    )}
                    {event.rules_pdf && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${event.rules_pdf}`}
                        download
                        className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Bases PDF
                      </a>
                    )}
                    {event.privacy_pdf && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${event.privacy_pdf}`}
                        download
                        className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Privacidad PDF
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Gallery Button - Only for registered users */}
              {isAlreadyRegistered && (
                <div className="border-t pt-3">
                  <Button
                    onClick={fetchGallery}
                    className="flex-1 md:flex-none bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700"
                    data-testid="view-gallery-btn"
                  >
                    <ImageIcon className="w-4 h-4 mr-2" />
                    Ver Galería de Fotos
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Map Section with Multiple Tracks */}
        {availableTracks.length > 0 && (
          <Card className="border-0 shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center gap-2">
                <MapPin className="w-6 h-6 text-orange-500" />
                Visualización de Rutas
              </CardTitle>
              <CardDescription>Mapa interactivo con los tracks del evento</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Track Controls */}
              <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="show_all_tracks"
                    checked={showAllTracks}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setShowAllTracks(checked);
                      setVisibleTracks(new Array(7).fill(checked));
                    }}
                    className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                  />
                  <Label htmlFor="show_all_tracks" className="font-semibold cursor-pointer">
                    Mostrar Todos los Tracks
                  </Label>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 ml-7">
                  {availableTracks.map((track, idx) => (
                    <div key={track.number} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`track_visible_${track.number}`}
                        checked={visibleTracks[track.number - 1]}
                        onChange={(e) => {
                          const newVisible = [...visibleTracks];
                          newVisible[track.number - 1] = e.target.checked;
                          setVisibleTracks(newVisible);
                          setShowAllTracks(newVisible.every(v => v));
                        }}
                        className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                      />
                      <Label htmlFor={`track_visible_${track.number}`} className="text-sm cursor-pointer">
                        {track.name}
                      </Label>
                      <a
                        href={getFileUrl(track.file)}
                        download
                        className="text-orange-600 hover:text-orange-800"
                        title="Descargar track"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Map */}
              <div className="w-full h-96 rounded-lg overflow-hidden border-2 border-gray-200 relative">
                {/* Map Style Selector */}
                <div className="absolute top-2 right-2 z-[1000]">
                  <MapSelector compact />
                </div>
                
                <MapContainer
                  center={[40.4168, -3.7038]}
                  zoom={6}
                  style={{ height: '100%', width: '100%' }}
                  scrollWheelZoom={true}
                >
                  <DynamicTileLayer />
                  <FullscreenControl />
                  <MultiGPXLayer tracks={availableTracks} visibleTracks={visibleTracks} />
                </MapContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Terms Dialog */}
        <Dialog open={showTerms} onOpenChange={setShowTerms}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="terms-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>Recomendaciones y Normas</span>
                {event.terms_pdf && (
                  <a 
                    href={`${process.env.REACT_APP_BACKEND_URL}${event.terms_pdf}`}
                    download
                    className="text-orange-600 hover:text-orange-800 flex items-center gap-1 text-sm"
                  >
                    <Download className="w-4 h-4" />
                    Descargar PDF
                  </a>
                )}
              </DialogTitle>
            </DialogHeader>
            <div className="prose prose-sm" data-testid="terms-content">
              <p className="whitespace-pre-wrap">{event.terms_conditions}</p>
            </div>
          </DialogContent>
        </Dialog>

        {/* Participation Rules Dialog */}
        <Dialog open={showRules} onOpenChange={setShowRules}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="rules-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>Bases de Participación</span>
                {event.rules_pdf && (
                  <a 
                    href={`${process.env.REACT_APP_BACKEND_URL}${event.rules_pdf}`}
                    download
                    className="text-orange-600 hover:text-orange-800 flex items-center gap-1 text-sm"
                  >
                    <Download className="w-4 h-4" />
                    Descargar PDF
                  </a>
                )}
              </DialogTitle>
            </DialogHeader>
            <div className="prose prose-sm" data-testid="rules-content">
              <p className="whitespace-pre-wrap">{event.participation_rules}</p>
            </div>
          </DialogContent>
        </Dialog>

        {/* Privacy Policy Dialog */}
        <Dialog open={showPrivacyPolicy} onOpenChange={setShowPrivacyPolicy}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="privacy-policy-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>Política de Privacidad y Uso de Imagen</span>
                {event.privacy_pdf && (
                  <a 
                    href={`${process.env.REACT_APP_BACKEND_URL}${event.privacy_pdf}`}
                    download
                    className="text-orange-600 hover:text-orange-800 flex items-center gap-1 text-sm"
                  >
                    <Download className="w-4 h-4" />
                    Descargar PDF
                  </a>
                )}
              </DialogTitle>
            </DialogHeader>
            <div className="prose prose-sm" data-testid="privacy-policy-content">
              <p className="whitespace-pre-wrap">{event.privacy_policy || 'No disponible'}</p>
            </div>
          </DialogContent>
        </Dialog>

        {/* Gallery Dialog */}
        <Dialog open={showGallery} onOpenChange={setShowGallery}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="gallery-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ImageIcon className="w-6 h-6 text-purple-600" />
                Galería de Fotos del Evento
              </DialogTitle>
              <DialogDescription>
                {galleryPhotos.length > 0 
                  ? `${galleryPhotos.length} foto(s) disponible(s)` 
                  : 'Aún no hay fotos disponibles'}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4">
              {galleryPhotos.length === 0 ? (
                <div className="text-center py-12">
                  <ImageIcon className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600">No hay fotos en la galería todavía</p>
                  <p className="text-sm text-gray-500 mt-2">El organizador subirá fotos pronto</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {galleryPhotos.map((photoUrl, index) => (
                    <div key={photoUrl} className="group relative">
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${photoUrl}`}
                        alt={`Foto ${index + 1}`}
                        className="w-full h-48 object-cover rounded-lg border-2 border-gray-200 hover:border-purple-500 transition-all cursor-pointer"
                        onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${photoUrl}`, '_blank')}
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-lg flex items-center justify-center">
                        <Download 
                          className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = `${process.env.REACT_APP_BACKEND_URL}${photoUrl}`;
                            link.download = `foto-${index + 1}.jpg`;
                            link.click();
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Registration Form - Only show if event is active and user is not already registered */}
        {event.status === 'completed' ? (
          <Card className="border-0 shadow-xl bg-gray-50">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-800">Evento Realizado</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center p-8 text-center">
                <div className="space-y-3">
                  <Badge className="bg-gray-500 text-white text-lg px-6 py-2">
                    ✓ Este evento ya se ha realizado
                  </Badge>
                  <p className="text-gray-700 text-lg">
                    Este evento ya finalizó. Puedes ver los detalles pero las inscripciones están cerradas.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : event.status === 'blocked' ? (
          <Card className="border-0 shadow-xl bg-yellow-50">
            <CardHeader>
              <CardTitle className="text-2xl text-yellow-800">Inscripciones Cerradas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center p-8 text-center">
                <div className="space-y-3">
                  <Badge className="bg-yellow-200 text-yellow-800 text-lg px-6 py-2">
                    🔒 Este evento está bloqueado
                  </Badge>
                  <p className="text-yellow-700 text-lg">
                    Ya no se admiten nuevas inscripciones
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : !isAlreadyRegistered ? (
          showRegistrationForm ? (
            <Card id="registro" className="border-0 shadow-xl" data-testid="registration-form-card">
            <CardHeader>
              <CardTitle className="text-2xl">Formulario de Inscripción</CardTitle>
              <CardDescription>Completa todos los datos para inscribirte en este evento</CardDescription>
            </CardHeader>

            <CardContent>
            <form 
              onSubmit={handleSubmit} 
              className="space-y-6"
            >
              {/* Personal Data */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Datos Personales</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Nombre y Apellidos *</Label>
                    <Input
                      id="full_name"
                      data-testid="registration-full-name"
                      value={formData.full_name}
                      onChange={(e) => {
                        setFormData({ ...formData, full_name: e.target.value });
                        setErrors(prev => ({ ...prev, full_name: undefined }));
                      }}
                      className={errors.full_name ? 'border-red-500' : ''}
                      required
                    />
                    {errors.full_name && <p className="text-sm text-red-600">⚠️ {errors.full_name}</p>}
                  </div>
                  
                  {/* DNI - Solo mostrar si es requerido */}
                  {event.requires_dni !== false && (
                    <div className="space-y-2">
                      <Label htmlFor="dni">DNI *</Label>
                      <Input
                        id="dni"
                        data-testid="registration-dni"
                        value={formData.dni}
                        onChange={(e) => {
                          setFormData({ ...formData, dni: e.target.value });
                          setErrors(prev => ({ ...prev, dni: undefined }));
                        }}
                        className={errors.dni ? 'border-red-500' : ''}
                        required
                      />
                      {errors.dni && <p className="text-sm text-red-600">⚠️ {errors.dni}</p>}
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Teléfono *</Label>
                    <Input
                      id="phone"
                      data-testid="registration-phone"
                      type="tel"
                      placeholder="+34 600 000 000"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      data-testid="registration-email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                  
                  {/* Dirección Física - Solo mostrar si es requerido */}
                  {event.requires_physical_address !== false && (
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="physical_address">Dirección Física *</Label>
                      <Input
                        id="physical_address"
                        data-testid="registration-address"
                        value={formData.physical_address}
                        onChange={(e) => setFormData({ ...formData, physical_address: e.target.value })}
                        required
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Motorcycle Selection */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Selecciona tu Motocicleta</h3>
                <div className="space-y-2">
                  <Label htmlFor="moto_selection">Motocicleta *</Label>
                  <select
                    id="moto_selection"
                    data-testid="moto-selection"
                    value={motoSelection}
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      setMotoSelection(selectedValue);
                      
                      // If selecting a saved motorcycle, fill in the data AND documents
                      if (selectedValue !== 'rental' && selectedValue !== 'other' && selectedValue !== 'none' && selectedValue !== '') {
                        const selectedMoto = motorcycles.find(m => m.id === selectedValue);
                        if (selectedMoto) {
                          setFormData({
                            ...formData,
                            moto_brand: selectedMoto.brand,
                            moto_model: selectedMoto.model,
                            moto_plate: selectedMoto.plate,
                            moto_years: selectedMoto.years?.toString() || '',
                            moto_kilometers: selectedMoto.kilometers?.toString() || ''
                          });
                          
                          // Load documents from saved motorcycle
                          const newFiles = { ...files };
                          if (selectedMoto.circulation_permit) {
                            newFiles.circulation_permit = selectedMoto.circulation_permit;
                          }
                          if (selectedMoto.technical_sheet) {
                            newFiles.technical_sheet = selectedMoto.technical_sheet;
                          }
                          if (selectedMoto.insurance) {
                            newFiles.insurance = selectedMoto.insurance;
                          }
                          setFiles(newFiles);
                          
                          console.log('Loaded documents from motorcycle:', selectedMoto);
                        }
                      } else if (selectedValue !== 'other') {
                        // Reset moto fields AND documents if selecting rental, none, or empty
                        setFormData({
                          ...formData,
                          moto_brand: '',
                          moto_model: '',
                          moto_plate: '',
                          moto_years: '',
                          moto_kilometers: ''
                        });
                        // Clear documents
                        setFiles({
                          circulation_permit: null,
                          technical_sheet: null,
                          insurance: null
                        });
                      }
                    }}
                    className="w-full h-10 px-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="">Selecciona una opción</option>
                    {motorcycles.map((moto) => (
                      <option key={moto.id} value={moto.id}>
                        {moto.brand} {moto.model} - {moto.plate}
                      </option>
                    ))}
                    <option value="rental">Moto de Alquiler</option>
                    <option value="other">Otra</option>
                    <option value="none">Sin Moto</option>
                  </select>
                </div>

                {/* Show selected motorcycle details if saved motorcycle is selected */}
                {motoSelection && motoSelection !== 'rental' && motoSelection !== 'other' && motoSelection !== 'none' && (
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <h4 className="font-semibold text-green-800 mb-2">✓ Motocicleta Seleccionada:</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                      <div><strong>Marca:</strong> {formData.moto_brand}</div>
                      <div><strong>Modelo:</strong> {formData.moto_model}</div>
                      <div><strong>Matrícula:</strong> {formData.moto_plate}</div>
                      {formData.moto_years && <div><strong>Año:</strong> {formData.moto_years}</div>}
                    </div>
                  </div>
                )}

                {/* Show fields if "Rental" is selected - all optional */}
                {motoSelection === 'rental' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg">
                    <div className="md:col-span-2">
                      <p className="text-sm text-blue-700 mb-3">
                        <strong>Nota:</strong> Para motos de alquiler, todos los campos son opcionales. 
                        Si conoces los datos de la moto, puedes completarlos ahora o más adelante.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="moto_brand_rental">Marca (opcional)</Label>
                      <Input
                        id="moto_brand_rental"
                        data-testid="registration-moto-brand-rental"
                        value={formData.moto_brand}
                        onChange={(e) => setFormData({ ...formData, moto_brand: e.target.value })}
                        placeholder="Ej: Honda, Yamaha..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="moto_model_rental">Modelo (opcional)</Label>
                      <Input
                        id="moto_model_rental"
                        data-testid="registration-moto-model-rental"
                        value={formData.moto_model}
                        onChange={(e) => setFormData({ ...formData, moto_model: e.target.value })}
                        placeholder="Ej: Africa Twin, Ténéré..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="moto_plate_rental">Matrícula (opcional)</Label>
                      <Input
                        id="moto_plate_rental"
                        data-testid="registration-moto-plate-rental"
                        value={formData.moto_plate}
                        onChange={(e) => setFormData({ ...formData, moto_plate: e.target.value })}
                        placeholder="Ej: 1234ABC"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="moto_years_rental">Año (opcional)</Label>
                      <Input
                        id="moto_years_rental"
                        data-testid="registration-moto-years-rental"
                        type="number"
                        value={formData.moto_years}
                        onChange={(e) => setFormData({ ...formData, moto_years: e.target.value })}
                        placeholder="2020"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="moto_kilometers_rental">Kilómetros (opcional)</Label>
                      <Input
                        id="moto_kilometers_rental"
                        data-testid="registration-moto-kilometers-rental"
                        type="number"
                        value={formData.moto_kilometers}
                        onChange={(e) => setFormData({ ...formData, moto_kilometers: e.target.value })}
                        placeholder="50000"
                      />
                    </div>
                  </div>
                )}

                {/* Show fields if "Otra" is selected - All optional */}
                {motoSelection === 'other' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-orange-50 rounded-lg">
                    <div className="space-y-2">
                      <Label htmlFor="moto_brand">Marca (opcional)</Label>
                      <Input
                        id="moto_brand"
                        data-testid="registration-moto-brand"
                        value={formData.moto_brand}
                        onChange={(e) => setFormData({ ...formData, moto_brand: e.target.value })}
                        placeholder="Ej: Honda, Yamaha..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="moto_model">Modelo (opcional)</Label>
                      <Input
                        id="moto_model"
                        data-testid="registration-moto-model"
                        value={formData.moto_model}
                        onChange={(e) => setFormData({ ...formData, moto_model: e.target.value })}
                        placeholder="Ej: Africa Twin, Ténéré..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="moto_plate">Matrícula (opcional)</Label>
                      <Input
                        id="moto_plate"
                        data-testid="registration-moto-plate"
                        value={formData.moto_plate}
                        onChange={(e) => setFormData({ ...formData, moto_plate: e.target.value })}
                        placeholder="Ej: 1234ABC"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="moto_years">Año (opcional)</Label>
                      <Input
                        id="moto_years"
                        data-testid="registration-moto-years"
                        type="number"
                        value={formData.moto_years}
                        onChange={(e) => setFormData({ ...formData, moto_years: e.target.value })}
                        placeholder="2020"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="moto_kilometers">Kilómetros (opcional)</Label>
                      <Input
                        id="moto_kilometers"
                        data-testid="registration-moto-kilometers"
                        type="number"
                        value={formData.moto_kilometers}
                        onChange={(e) => setFormData({ ...formData, moto_kilometers: e.target.value })}
                        placeholder="50000"
                      />
                    </div>
                  </div>
                )}

                {/* Show message if "Sin Moto" is selected */}
                {motoSelection === 'none' && (
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-700">
                      <strong>📋 Nota:</strong> Has seleccionado participar sin moto propia. 
                      No es necesario proporcionar información sobre la motocicleta.
                    </p>
                  </div>
                )}
              </div>

              {/* Documents - Solo mostrar si el evento requiere documentos Y el usuario tiene moto */}
              {event.requires_documents !== false && motoSelection !== 'none' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Documentación *
                  </h3>
                  <p className="text-sm text-gray-600">Los documentos son obligatorios para este evento</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Circulation Permit */}
                  <div className="space-y-2">
                    <Label htmlFor="circulation_permit">Permiso de Circulación</Label>
                    <div className="relative">
                      <Input
                        id="circulation_permit"
                        data-testid="registration-circulation-permit"
                        type="file"
                        onChange={(e) => handleFileChange('circulation_permit', e.target.files[0])}
                        required={event.requires_documents !== false && !files.circulation_permit && motoSelection !== 'none'}
                        className="cursor-pointer"
                      />
                      {files.circulation_permit && typeof files.circulation_permit !== 'string' && (
                        <Check className="absolute right-3 top-3 w-5 h-5 text-green-500" />
                      )}
                    </div>
                    {files.circulation_permit && typeof files.circulation_permit === 'string' && (
                      <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-md">
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <span className="text-xs text-green-700 flex-1">Usando documento de tu moto guardada</span>
                        <a 
                          href={`${process.env.REACT_APP_BACKEND_URL}${files.circulation_permit}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 hover:text-green-800"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Technical Sheet */}
                  <div className="space-y-2">
                    <Label htmlFor="technical_sheet">Ficha Técnica</Label>
                    <div className="relative">
                      <Input
                        id="technical_sheet"
                        data-testid="registration-technical-sheet"
                        type="file"
                        onChange={(e) => handleFileChange('technical_sheet', e.target.files[0])}
                        required={event.requires_documents !== false && !files.technical_sheet && motoSelection !== 'none'}
                        className="cursor-pointer"
                      />
                      {files.technical_sheet && typeof files.technical_sheet !== 'string' && (
                        <Check className="absolute right-3 top-3 w-5 h-5 text-green-500" />
                      )}
                    </div>
                    {files.technical_sheet && typeof files.technical_sheet === 'string' && (
                      <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-md">
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <span className="text-xs text-green-700 flex-1">Usando documento de tu moto guardada</span>
                        <a 
                          href={`${process.env.REACT_APP_BACKEND_URL}${files.technical_sheet}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 hover:text-green-800"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Insurance */}
                  <div className="space-y-2">
                    <Label htmlFor="insurance">Seguro</Label>
                    <div className="relative">
                      <Input
                        id="insurance"
                        data-testid="registration-insurance"
                        type="file"
                        onChange={(e) => handleFileChange('insurance', e.target.files[0])}
                        required={event.requires_documents !== false && !files.insurance && motoSelection !== 'none'}
                        className="cursor-pointer"
                      />
                      {files.insurance && typeof files.insurance !== 'string' && (
                        <Check className="absolute right-3 top-3 w-5 h-5 text-green-500" />
                      )}
                    </div>
                    {files.insurance && typeof files.insurance === 'string' && (
                      <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-md">
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <span className="text-xs text-green-700 flex-1">Usando documento de tu moto guardada</span>
                        <a 
                          href={`${process.env.REACT_APP_BACKEND_URL}${files.insurance}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 hover:text-green-800"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      </div>
                    )}
                  </div>
                </div>
                </div>
              )}

              {/* Odometer Motorcycles Section */}
              {event.requires_odometer_photo && (
                <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-blue-900">
                      📸 Fotografía del Cuentakilómetros
                    </h3>
                    <span className="text-sm text-blue-600">Hasta 5 motos</span>
                  </div>
                  <p className="text-sm text-blue-700">
                    Este evento requiere que registres tus motos con una foto del cuentakilómetros.
                  </p>
                  
                  {odometerMotos.map((moto, index) => (
                    <div key={`moto-slot-${index}`} className="p-4 bg-white rounded-lg border border-blue-100 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-700">Moto {index + 1}</span>
                        {index > 0 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newMotos = odometerMotos.filter((_, i) => i !== index);
                              setOdometerMotos(newMotos);
                            }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            Eliminar
                          </Button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <Label>Marca *</Label>
                          <Input
                            value={moto.brand}
                            onChange={(e) => {
                              const newMotos = [...odometerMotos];
                              newMotos[index].brand = e.target.value;
                              setOdometerMotos(newMotos);
                            }}
                            placeholder="Ej: Honda"
                            required={index === 0}
                          />
                        </div>
                        <div>
                          <Label>Modelo *</Label>
                          <Input
                            value={moto.model}
                            onChange={(e) => {
                              const newMotos = [...odometerMotos];
                              newMotos[index].model = e.target.value;
                              setOdometerMotos(newMotos);
                            }}
                            placeholder="Ej: Africa Twin"
                            required={index === 0}
                          />
                        </div>
                        <div>
                          <Label>Kilómetros *</Label>
                          <Input
                            type="number"
                            value={moto.kilometers}
                            onChange={(e) => {
                              const newMotos = [...odometerMotos];
                              newMotos[index].kilometers = e.target.value;
                              setOdometerMotos(newMotos);
                            }}
                            placeholder="Ej: 25000"
                            required={index === 0}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label>Foto del Cuentakilómetros *</Label>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const newMotos = [...odometerMotos];
                            newMotos[index].photo = e.target.files[0];
                            setOdometerMotos(newMotos);
                          }}
                          required={index === 0}
                          className="cursor-pointer"
                        />
                        {moto.photo && (
                          <p className="text-xs text-green-600 mt-1">✓ {moto.photo.name}</p>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {odometerMotos.length < 5 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setOdometerMotos([...odometerMotos, { brand: '', model: '', kilometers: '', photo: null }]);
                      }}
                      className="w-full border-dashed border-blue-300 text-blue-600 hover:bg-blue-50"
                    >
                      + Añadir otra moto
                    </Button>
                  )}
                </div>
              )}

              {/* Discount Code */}
              {event.discount_codes && event.discount_codes.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="discount_code">Código de Descuento (opcional)</Label>
                  <Input
                    id="discount_code"
                    data-testid="registration-discount-code"
                    value={formData.discount_code}
                    onChange={(e) => setFormData({ ...formData, discount_code: e.target.value })}
                    placeholder="Introduce tu código"
                  />
                </div>
              )}

              {/* Optional Fields: IBA, AREM, AEMOTUR, T-Shirt Size */}
              {(event.requires_iba_number || event.requires_arem_number || event.requires_aemotur_number || event.requires_tshirt_size) && (
                <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="text-sm font-semibold text-blue-900">Datos Adicionales</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {event.requires_iba_number && (
                      <div className="space-y-2">
                        <Label htmlFor="iba_number">Número IBA</Label>
                        <Input
                          id="iba_number"
                          data-testid="registration-iba-number"
                          value={formData.iba_number}
                          onChange={(e) => setFormData({ ...formData, iba_number: e.target.value })}
                          placeholder="Tu número de socio IBA"
                        />
                      </div>
                    )}
                    
                    {event.requires_arem_number && (
                      <div className="space-y-2">
                        <Label htmlFor="arem_number">Número AREM</Label>
                        <Input
                          id="arem_number"
                          data-testid="registration-arem-number"
                          value={formData.arem_number}
                          onChange={(e) => setFormData({ ...formData, arem_number: e.target.value })}
                          placeholder="Tu número de socio AREM"
                        />
                      </div>
                    )}
                    
                    {event.requires_aemotur_number && (
                      <div className="space-y-2">
                        <Label htmlFor="aemotur_number">Número AEMOTUR</Label>
                        <Input
                          id="aemotur_number"
                          data-testid="registration-aemotur-number"
                          value={formData.aemotur_number}
                          onChange={(e) => setFormData({ ...formData, aemotur_number: e.target.value })}
                          placeholder="Tu número de socio AEMOTUR"
                        />
                      </div>
                    )}
                    
                    {event.requires_tshirt_size && (
                      <div className="space-y-2">
                        <Label htmlFor="tshirt_size">Talla de Camiseta</Label>
                        <select
                          id="tshirt_size"
                          data-testid="registration-tshirt-size"
                          value={formData.tshirt_size}
                          onChange={(e) => setFormData({ ...formData, tshirt_size: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        >
                          <option value="">Selecciona talla</option>
                          <option value="XS">XS</option>
                          <option value="S">S</option>
                          <option value="M">M</option>
                          <option value="L">L</option>
                          <option value="XL">XL</option>
                          <option value="XXL">XXL</option>
                          <option value="XXXL">XXXL</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Acceptances */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-900">Aceptaciones Obligatorias *</h3>
                
                {/* 1. Terms Acceptance */}
                <div className="flex items-start gap-3 p-4 bg-orange-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="accept_terms"
                    data-testid="registration-accept-terms"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                    required
                  />
                  <Label htmlFor="accept_terms" className="text-sm cursor-pointer">
                    He leído y acepto las <button type="button" onClick={() => setShowTerms(true)} className="text-orange-600 underline font-semibold">Recomendaciones y Normas</button> *
                  </Label>
                </div>

                {/* 2. Rules Acceptance */}
                <div className="flex items-start gap-3 p-4 bg-orange-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="accept_rules"
                    data-testid="registration-accept-rules"
                    checked={acceptedRules}
                    onChange={(e) => setAcceptedRules(e.target.checked)}
                    className="mt-1 w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                    required
                  />
                  <Label htmlFor="accept_rules" className="text-sm cursor-pointer">
                    He leído y acepto las <button type="button" onClick={() => setShowRules(true)} className="text-orange-600 underline font-semibold">Bases de Participación</button> *
                  </Label>
                </div>

                {/* 3. Privacy Acceptance */}
                <div className="flex items-start gap-3 p-4 bg-orange-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="accept_privacy"
                    data-testid="registration-accept-privacy"
                    checked={acceptedPrivacy}
                    onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                    className="mt-1 w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                    required
                  />
                  <Label htmlFor="accept_privacy" className="text-sm cursor-pointer">
                    He leído y acepto la <button type="button" onClick={() => setShowPrivacyPolicy(true)} className="text-orange-600 underline font-semibold">Política de Privacidad y Uso de Imagen</button> *
                  </Label>
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={registering || !acceptedTerms || !acceptedPrivacy || !acceptedRules}
                className="w-full h-12 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold text-lg"
                data-testid="registration-submit-btn"
              >
                {registering ? 'Procesando...' : 'Inscribirme al Evento'}
              </Button>
            </form>
          </CardContent>
        </Card>
          ) : (
            <Card id="registro" className="border-0 shadow-xl">
              <CardContent className="py-12 text-center">
                <div className="flex flex-col items-center gap-4">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">¿Quieres inscribirte en este evento?</h3>
                  <p className="text-gray-600 mb-4">
                    Haz clic en el botón para ver el formulario de inscripción
                  </p>
                  <Button
                    onClick={() => setShowRegistrationForm(true)}
                    className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold px-8 py-3 text-lg"
                  >
                    Mostrar Formulario de Inscripción
                  </Button>
                </div>
              </CardContent>
            </Card>
          )
        ) : (
          <Card className="border-0 shadow-xl bg-green-50">
            <CardContent className="py-12 text-center">
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">Ya estás inscrito en este evento</h3>
                  <p className="text-gray-600 mb-4">
                    Puedes ver todos los detalles del evento y descargar los archivos desde tu panel de inscripciones.
                  </p>
                  <Button
                    onClick={() => navigate('/my-registrations')}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    Ver Mis Inscripciones
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
